import { FC } from "react";
import { ShareIssuePerShareTypeModel } from "./increaseOfShareCapitalDomain";
import { observer } from "mobx-react";
import { Box, Grid, GridItem } from "@/components-v2/Box";
import { FixedOrInterval, FixedOrInterval2 } from "./FixedOrInterval";
import { NumberField } from "@/dbot/common/commonComponents";
import { CURRENCY_NOK } from "@/utils/currency";
import { useTranslation } from "@/stores/RootStoreContext";
import { findShareTypeName } from "@/common/share";
import { MCheckbox } from "@/components-v2/Checkbox";
import { InfoButton } from "@/components-v2/InfoButton";
import { useElementId } from "@/utils/reactHelpers";

const ShareIssueForShareType: FC<{
  model: ShareIssuePerShareTypeModel;
  isLocked: boolean;
  disableIndividualSharePrice: boolean;
}> = observer(({ model, isLocked, disableIndividualSharePrice }) => {
  const { t, lang } = useTranslation();
  return (
    <>
      <div>
        {t("dbot.sharecapitalIncrease.forSharetype", {
          shareType: findShareTypeName(model.shareType, lang),
        })}
      </div>

      <FixedOrInterval form={model} isLocked={isLocked} />

      <ShareIssueForShareTypeInputs
        model={model}
        isLocked={isLocked}
        disableIndividualSharePrice={disableIndividualSharePrice}
      />

      <hr />
    </>
  );
});

export const ShareIssueForShareType2: FC<{
  model: ShareIssuePerShareTypeModel;
  isLocked: boolean;
  disableIndividualSharePrice: boolean;
}> = observer(({ model, isLocked, disableIndividualSharePrice }) => {
  return (
    <>
      <FixedOrInterval2 form={model} isLocked={isLocked} />
      <ShareIssueForShareTypeInputs
        model={model}
        isLocked={isLocked}
        disableIndividualSharePrice={disableIndividualSharePrice}
      />
    </>
  );
});

const ShareIssueForShareTypeInputs: FC<{
  model: ShareIssuePerShareTypeModel;
  isLocked: boolean;
  disableIndividualSharePrice: boolean;
}> = observer(({ model, isLocked, disableIndividualSharePrice }) => {
  const { t, lang } = useTranslation();
  const numberOfSharesId = useElementId();
  const sharePriceId = useElementId();

  return (
    <>
      {model.showShareIssueFixed && (
        <Grid cols={2} alignItems="center" columnGap={"lg"}>
          <GridItem colSpan={2}>
            <label htmlFor={numberOfSharesId}>{t("decisions.number-of-shares")}</label>
          </GridItem>
          <NumberField
            id={numberOfSharesId}
            field={model.fixed.numberOfShares}
            onBlur={model.onChange}
            isLocked={isLocked}
            disabled={model.fixed.calculateFromSubscriptions.value}
          />
          <Box>
            <MCheckbox
              label={t("decisions.increaseShareCapitalAgainstCash.newShares.asAllocated")}
              model={model.fixed.calculateFromSubscriptions}
            />
            <InfoButton
              infoText={t("decisions.increaseShareCapitalAgainstCash.newShares.asAllocated.info")}
            />
          </Box>
        </Grid>
      )}

      {model.showShareIssueInterval && (
        <Grid cols={2} alignItems="center" columnGap={"lg"}>
          <NumberField
            label={t("decisions.from")}
            field={model.interval.from}
            isLocked={isLocked}
          />
          <NumberField
            label={t("decisions.to")}
            field={model.interval.to}
            onBlur={model.onChange}
            isLocked={isLocked}
          />
        </Grid>
      )}

      <Grid cols={2} alignItems={"center"} columnGap={"lg"}>
        <GridItem colSpan={2}>
          <label htmlFor={sharePriceId}>
            {t("decisions.increaseShareCapitalAgainstCash.newShares.subscriptionValue.title", {
              shareType: findShareTypeName(model.shareType, lang, { singular: true }),
            })}
          </label>
        </GridItem>
        <NumberField
          id={sharePriceId}
          name={"sharePrice"}
          prefix={CURRENCY_NOK}
          field={model.sharePrice}
          isLocked={isLocked}
        />
        <Box
          tooltip={
            disableIndividualSharePrice
              ? t(
                  "decisions.increaseShareCapitalAgainstCash.newShares.subscriptionValue.individualize.disabled.isPreferentialRights",
                )
              : undefined
          }
        >
          <MCheckbox
            model={model.isIndividualSharePrice}
            disabled={disableIndividualSharePrice}
            onChange={(value) => {
              model.isIndividualSharePrice.setValue(value.target.checked);
              model.onChange();
            }}
            label={t(
              "decisions.increaseShareCapitalAgainstCash.newShares.subscriptionValue.individualize.label",
            )}
          />
        </Box>
      </Grid>
    </>
  );
});

export default ShareIssueForShareType;
